import { useEffect, useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PRACTICE_ITEMS, getPracticePath } from "../../practice/config";
import { getLanguageFromPath } from "../../languages";
import {
  CoursesNavItem,
  CoursesNavTrigger,
  CoursesDropdown,
  CoursesDropdownPanel,
  CoursesDropdownLink,
  CoursesMobileList,
  CoursesMobileExpandButton,
  CoursesMobileTopRow,
  CoursesMobileToggle,
  CoursesMobileSubmenu,
  CoursesMobileSubLink,
} from "./styles";

type PracticeNavProps = {
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
  drawerOpen?: boolean;
};

const PracticeNav = ({
  variant = "desktop",
  onNavigate,
  drawerOpen = false,
}: PracticeNavProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const currentLanguage = getLanguageFromPath(location.pathname);

  useEffect(() => {
    if (!drawerOpen) {
      setMobileExpanded(false);
    }
  }, [drawerOpen]);

  const handleNavigate = () => {
    setDesktopOpen(false);
    setMobileExpanded(false);
    onNavigate?.();
  };

  if (variant === "mobile") {
    return (
      <CoursesMobileList>
        <CoursesMobileTopRow>
          <CoursesMobileToggle
            type="button"
            aria-expanded={mobileExpanded}
            onClick={() => setMobileExpanded((expanded) => !expanded)}
          >
            {t("practice.nav")}
          </CoursesMobileToggle>
          <CoursesMobileExpandButton
            type="button"
            aria-label={t("practice.mobile.toggle")}
            aria-expanded={mobileExpanded}
            onClick={() => setMobileExpanded((expanded) => !expanded)}
          >
            <DownOutlined />
          </CoursesMobileExpandButton>
        </CoursesMobileTopRow>
        {mobileExpanded && (
          <CoursesMobileSubmenu>
            {PRACTICE_ITEMS.map((item) => (
              <CoursesMobileSubLink
                key={item.slug}
                to={getPracticePath(item.slug, currentLanguage)}
                onClick={handleNavigate}
              >
                {t(item.labelKey)}
              </CoursesMobileSubLink>
            ))}
          </CoursesMobileSubmenu>
        )}
      </CoursesMobileList>
    );
  }

  return (
    <CoursesNavItem
      onMouseEnter={() => setDesktopOpen(true)}
      onMouseLeave={() => setDesktopOpen(false)}
    >
      <CoursesNavTrigger>{t("practice.nav")}</CoursesNavTrigger>
      <CoursesDropdown $open={desktopOpen}>
        <CoursesDropdownPanel>
          {PRACTICE_ITEMS.map((item) => (
            <CoursesDropdownLink
              key={item.slug}
              to={getPracticePath(item.slug, currentLanguage)}
              onClick={handleNavigate}
            >
              {t(item.labelKey)}
            </CoursesDropdownLink>
          ))}
        </CoursesDropdownPanel>
      </CoursesDropdown>
    </CoursesNavItem>
  );
};

export default PracticeNav;
